import {
  DataRoomFolderFragmentFragment,
  SearchDealQuery,
  SearchDealResultType,
} from "@/src/graphql/generated";
import { useLocation } from "react-router-dom";
import { truncateText } from "@/src/utils/truncateText";
import { classNames } from "@/src/utils/cn";
import { useState } from "react";
import {
  MessageCircleQuestionIcon,
  FolderIcon as FolderIconOutline,
  FilePlusIcon,
  ListIcon,
  MessageCirclePlusIcon,
  FileIcon as FileIconOutline,
  FolderOpenIcon,
  LockIcon,
} from "lucide-react";
import clsx from "clsx";
import { KeyboardShortcut } from "../../keyboard_shortcut";
import { SearchResultEntity } from "./search_result_entity";
import { SearchResultChunk } from "./search_result_chunk";

interface Props {
  result: SearchDealQuery["searchDeal"][0];
  searchTerm: string;
  isFocused: boolean;
}

export function SearchResult(props: Props) {
  return (
    <div className="flex items-center justify-between w-full">
      <SearchResultItem {...props} />
      <div
        className={clsx(
          props.isFocused ? "visible" : "invisible",
          "flex-shrink-0",
        )}
      >
        <KeyboardShortcut shortcut={`Enter \u21B5`} />
      </div>
    </div>
  );
}

function SearchResultItem(props: Props) {
  const { result, searchTerm, isFocused } = props;

  if (!result) {
    return null;
  }

  if (result.resultType === SearchDealResultType.Entity) {
    return (
      <SearchResultEntity
        result={result.entity}
        searchTerm={searchTerm}
        isFocused={isFocused}
      />
    );
  }

  if (result.resultType === SearchDealResultType.Chunk) {
    return (
      <SearchResultChunk
        result={result.chunk}
        searchTerm={searchTerm}
        isFocused={isFocused}
      />
    );
  }

  return null;
}

export function SeparationDot() {
  return <p className="text-xs text-gray-500">{`\u2022`}</p>;
}

export function ResultItemHeaderWrapper(props: { children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-x-1 py-1 -mt-1">{props.children}</div>
  );
}

export function ParentFolder(props: {
  folder?: Pick<DataRoomFolderFragmentFragment, "name" | "id"> | null;
}) {
  if (!props.folder) {
    return null;
  }

  return (
    <div className="flex items-center gap-x-1">
      <FolderOpenIcon className="w-3 h-3 text-gray-500" />
      <p className="text-xs/none text-gray-500">
        {props.folder.name === ""
          ? "Home"
          : truncateText(props.folder.name, 85)}
      </p>
    </div>
  );
}

export function SearchResultCategory(props: {
  category:
    | "file"
    | "folder"
    | "questions"
    | "answer"
    | "answer_file"
    | "access"
    | "ddql";
}) {
  switch (props.category) {
    case "folder":
      return (
        <div className="bg-gray-200 border border-gray-200 relative  py-1 rounded-full flex items-center justify-center gap-x-1 px-2">
          <FolderIconOutline className="w-3.5 h-3.5 text-gray-500" />
        </div>
      );
    case "file":
      return (
        <div className="bg-gray-200 border border-gray-200 relative  py-1 rounded-full flex items-center justify-center gap-x-1 px-2">
          <FileIconOutline className="w-3.5 h-3.5 text-gray-500" />
        </div>
      );
    case "questions":
      return (
        <div className="bg-gray-200 border border-gray-200 relative  py-1 rounded-full flex items-center justify-center gap-x-1 px-2">
          <MessageCircleQuestionIcon className="w-3.5 h-3.5 text-gray-500" />
        </div>
      );
    case "ddql":
      return (
        <div className="bg-gray-200 border border-gray-200 relative py-1 rounded-full flex items-center justify-center gap-x-1 px-2">
          <ListIcon className="w-3.5 h-3.5 text-gray-500" />
        </div>
      );
    case "answer":
      return (
        <div className="bg-gray-200 border border-gray-200 relative py-1 rounded-full flex items-center justify-center gap-x-1 px-2">
          <MessageCirclePlusIcon className="w-3.5 h-3.5 text-gray-500" />
        </div>
      );
    case "answer_file":
      return (
        <div className="bg-gray-200 border border-gray-200 relative py-1 rounded-full flex items-center justify-center gap-x-1 px-2">
          <FilePlusIcon className="w-3.5 h-3.5 text-gray-500" />
        </div>
      );
    case "access":
      return (
        <div className="bg-gray-200 border border-gray-200 relative py-1 rounded-full flex items-center justify-center gap-x-1 px-2">
          <LockIcon className="w-3.5 h-3.5 text-gray-500" />
        </div>
      );

    default:
      return null;
  }
}

interface BreadcrumbProps {
  folders: DataRoomFolderFragmentFragment["parentFolders"];
  root: string;
  p?: string;
}

export function Breadcrumbs(props: BreadcrumbProps) {
  const location = useLocation();

  const [parentFolders, setParentFolders] = useState<
    DataRoomFolderFragmentFragment["parentFolders"]
  >(props.folders);

  const crumbs: {
    name: string;
    link: string;
  }[] = [
    {
      name: "Home",
      link: `/${props.root}`,
    },
    ...parentFolders
      .slice(0, parentFolders.length - 1)
      .map((folder) => {
        return {
          name: folder.name,
          link: `/${props.root}/folder/${folder.id}`,
        };
      })
      .reverse(),
  ];

  return (
    <div
      className={`flex ${props.p ? props.p : "py-1"} gap-x-1 overflow-y-scroll no-scrollbar max-w-[500px]`}
    >
      {crumbs.map((folder, index) => {
        return (
          <div className="flex gap-x-1" key={folder.link}>
            <p
              title={folder.name}
              className={classNames(
                "m-0 font-semibold text-xs truncate text-gray-500",
              )}
            >
              {truncateText(folder.name, 70)}
            </p>
            {index !== props.folders.length - 1 ? (
              <p className="text-sm text-gray-400">/</p>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
